(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory(require('localforage-removeitems')) :
    typeof define === 'function' && define.amd ? define(['localforage-removeitems'], factory) :
    (global.localforageLruDriver = factory(global.localforageRemoveitems));
}(this, function (localforageRemoveitems) { 'use strict';

    var _baseMethods = {};

    var localforageMethods = ['_initStorage', 'clear', 'getItem', 'iterate', 'key', 'keys', 'length', 'removeItem', 'setItem'];

    /**
     * Adds base methods that were not overrided
     * Puts overrided base methods to _baseMethods container
     * @param {LocalForage} lfInstance - instance to add methods to
     * @param {Object} driver - new driver object
     * @param {Object} baseDriver - driver to draw base methods from
     */
    function _wireUpMethods(lfInstance, driver, baseDriver) {
        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;

        try {
            for (var _iterator = localforageMethods[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                var method = _step.value;

                if (!driver.hasOwnProperty(method) || driver[method].prototype.dummy) {
                    lfInstance[method] = baseDriver[method];
                } else {
                    _baseMethods[method] = baseDriver[method];
                }
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally {
            try {
                if (!_iteratorNormalCompletion && _iterator.return) {
                    _iterator.return();
                }
            } finally {
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    }

    /**
     * Returns a base method. Can only be called after _wireUpMethods is complete
     * @param {String} method - method name
     * @return {Function} method
     */
    function _getBaseMethod(method) {
        return _baseMethods[method];
    }

    /**
     * HACK: fills driver with empty functions
     * so that driver creation can be a sync process
     *
     * The problem is that LocalForage.prototype.getDriver is async
     * and base driver cannot be acquired in synchronous code
     * @param {Object} driver
     */
    function _wireUpDummyMethods(driver) {
        var _iteratorNormalCompletion2 = true;
        var _didIteratorError2 = false;
        var _iteratorError2 = undefined;

        try {
            for (var _iterator2 = localforageMethods[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
                var method = _step2.value;

                if (!driver.hasOwnProperty(method)) {
                    driver[method] = dummy;
                }
            }
        } catch (err) {
            _didIteratorError2 = true;
            _iteratorError2 = err;
        } finally {
            try {
                if (!_iteratorNormalCompletion2 && _iterator2.return) {
                    _iterator2.return();
                }
            } finally {
                if (_didIteratorError2) {
                    throw _iteratorError2;
                }
            }
        }
    }

    /**
     * Returns length of an index
     * @param {IDBIndex} index
     * @param {function(err, result)} callback
     */
    function _getIndexLength(index, callback) {
        var request = index.count();

        request.onsuccess = function () {
            return callback(null, request.result);
        };
        request.onerror = callback;
    }

    function dummy() {}
    dummy.prototype.dummy = true;

    var lruDriver = {
        _driver: 'lruStorage',
        _initStorage: function _initStorage(options) {
            var _this = this;

            this._lruIndex = options.lruIndex || 'lruIndex';
            this._cacheSize = options.cacheSize || 1000;
            this._lruKey = options.lruKey || 'ACCESS_TIME';

            return this.getDriver(this.INDEXEDDB).then(function (baseDriver) {
                return _wireUpMethods(_this, lruDriver, baseDriver);
            }).then(function () {
                return _getBaseMethod('_initStorage').call(_this, options);
            });
        },
        setItem: function setItem(key, value, callback) {
            var _this2 = this;

            return new Promise(function (resolve, reject) {
                var indexCb = function indexCb(err, index) {
                    if (err) return reject(err);

                    _limitCacheSize.call(_this2, index).then(function () {
                        return _updateEntry.call(_this2, key, value, callback);
                    }).then(resolve).catch(reject);
                };

                _this2.getIndex(_this2._lruIndex, function (err, index) {
                    return _ensureIndex.call(_this2, err, index, indexCb);
                });
            });
        },
        getItem: function getItem(key, callback) {
            var _this3 = this;

            return _getBaseMethod('getItem').call(this, key).then(function (value) {
                return value === null ? null : _updateEntry.call(_this3, key, value.data, callback);
            });
        }
    };

    /**
     * Removes least recently accessed items that exceed cache size
     * @param {IDBIndex} index - access time index
     * @return {Promise}
     */
    function _limitCacheSize(index) {
        var _this4 = this;

        return new Promise(function (resolve, reject) {
            return _getIndexLength(index, function (err, length) {
                if (err) return reject(err);

                if (length < _this4._cacheSize) return resolve();
                var request = index.getAllKeys();

                request.onerror = reject;
                request.onsuccess = function () {
                    var keys = request.result;

                    localforageRemoveitems.localforageRemoveItems.call(_this4, keys.slice(0, keys.length - _this4._cacheSize + 1)).then(function () {
                        return resolve();
                    }, reject);
                };
            });
        });
    }

    /**
     * Creates index if it doesn't exist, updates index if it has wrong keypath
     * @param {Error} err - truthy if index was not found
     * @param {IDBIndex} index
     * @param {function(err, index)} callback
     */
    function _ensureIndex(err, index, callback) {
        // HACK: index presence should be checked inside _initStorage function
        // but indexes can only be used after _initStorage

        // index does not exist
        if (err) return this.createIndex(this._lruIndex, this._lruKey, callback);

        // index has correct key
        if (index.keyPath === this._lruKey) return callback(null, index);

        // index has wrong key
        this.updateIndex(this._lruIndex, this._lruKey, callback);
    }

    /**
     * Updates entry's access time and data
     * @param {string} key - keypath of entry
     * @param {any} data - entry's new data
     * @param {function(err, data)} callback gets called with entry's data
     * @return {Promise<data>} resolves with entry's data
     */
    function _updateEntry(key, data, callback) {
        var _callback;
        var updatedValue = { data: data };
        updatedValue[this._lruKey] = Date.now();

        if (typeof callback === 'function') {
            _callback = function _callback(err, entry) {
                return callback(err, entry.data);
            };
        }

        return _getBaseMethod('setItem').call(this, key, updatedValue, _callback).then(function (entry) {
            return entry.data;
        });
    }

    _wireUpDummyMethods(lruDriver);

    return lruDriver;

}));


//////////////////
// WEBPACK FOOTER
// ./node_modules/localforage-lru-driver/dist/localforage-lru-driver.js
// module id = 1781
// module chunks = 0